
import { computed, defineComponent, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import PeriodontalMap from '@/pages/PeriodontalPage/components/PeriodotalMap.vue'
import PeriodontalPreview from '@/pages/PeriodontalPage/components/PeriodontalPreview.vue'
import PeriodontalPdf from '@/pages/PeriodontalPage/components/pdf/PeriodontalPdf.vue'
import { WConsolidatedReport } from '@/widgets/WConsolidatedReport'
import { WReportHeader } from '@/widgets/WReportHeader'
import { WReportSubmit } from '@/widgets/WReportSubmit'
import { WReportComments } from '@/widgets/WReportComments'
import {
  getDateFormat,
  PeriodontalData,
  showErrorMessage,
} from '@/shared/utils'
import {
  CommentTemplateType,
  CommentType,
  ConsolidatedDataType,
  ConsolidatedTableType,
  FullPeriodontalType,
  PeriodontalDetailType,
  PeriodontalReportType,
  RemoteCommentsType,
} from '@/shared/types'
import { DATE_FORMAT, JAW_TYPES, REPORT_VIEW } from '@/shared/constants'
import { usePeriodontalReport } from '@/shared/composables'
import {
  organizationsApi,
  patientApi,
  periodontalReportApi,
  userApi,
} from '@/shared/api'

export default defineComponent({
  name: 'PeriodontalPage',
  components: {
    WReportComments,
    WReportHeader,
    PeriodontalMap,
    WConsolidatedReport,
    PeriodontalPreview,
    WReportSubmit,
    PeriodontalPdf,
  },

  setup() {
    const { isReportUpdated, setUpdateReport } = usePeriodontalReport()

    const route = useRoute()

    const periodontalData = ref<PeriodontalReportType>({
      firstFacialData: new PeriodontalData(JAW_TYPES.jawUp),
      firstTongueData: new PeriodontalData(JAW_TYPES.jawUp, true),
      secondFacialData: new PeriodontalData(JAW_TYPES.jawDown),
      secondTongueData: new PeriodontalData(JAW_TYPES.jawDown),
      firstFacialDiagram: undefined,
      firstTongueDiagram: undefined,
      secondFacialDiagram: undefined,
      secondTongueDiagram: undefined,
    })

    const consolidatedData = ref<ConsolidatedDataType>({
      diabetes: false,
      smoking: false,
    })

    const reportInfo = ref<PeriodontalDetailType>({
      id: 0,
      date_at: '',
      organization: undefined,
      doctor: undefined,
    })

    const loading = ref(false)
    const comments = ref<CommentType[]>([])
    const commentTemplates = ref<CommentTemplateType[]>([])
    const email = ref('')

    const date = computed((): string =>
      reportInfo.value.date_at
        ? getDateFormat(new Date(reportInfo.value.date_at), DATE_FORMAT.dmyLong)
        : ''
    )

    const consolidatedPlaque = computed(() => {
      const value =
        periodontalData.value?.firstFacialData.getActivePlaque() +
        periodontalData.value?.firstTongueData.getActivePlaque() +
        periodontalData.value?.secondFacialData.getActivePlaque() +
        periodontalData.value?.secondTongueData.getActivePlaque()

      return Math.round((value * 100) / 192)
    })

    const consolidatedBleeding = computed(() => {
      const value =
        periodontalData.value?.firstFacialData.getActiveBleeding() +
        periodontalData.value?.firstTongueData.getActiveBleeding() +
        periodontalData.value?.secondFacialData.getActiveBleeding() +
        periodontalData.value?.secondTongueData.getActiveBleeding()

      return Math.round((value * 100) / 192)
    })

    const consolidatedPockets = computed(() => {
      return (
        Number(consolidatedPocketsTable.value[1].four) +
        Number(consolidatedPocketsTable.value[1].five) +
        Number(consolidatedPocketsTable.value[1].moreSix)
      )
    })

    const consolidatedPocketsTable = computed((): ConsolidatedTableType[] => [
      {
        four: pocketFour.value,
        five: pocketFive.value,
        moreSix: pocketMoreSix.value,
      },
      {
        four: Number(Math.round((pocketFour.value * 100) / 192).toFixed(2)),
        five: Number(Math.round((pocketFive.value * 100) / 192).toFixed(2)),
        moreSix: Number(
          Math.round((pocketMoreSix.value * 100) / 192).toFixed(2)
        ),
      },
      {
        four: Number(
          (
            Math.round((pocketFour.value * 100) / 192) +
            Math.round((pocketFive.value * 100) / 192) +
            Math.round((pocketMoreSix.value * 100) / 192)
          ).toFixed(2)
        ),
        five: Number(
          (
            Math.round((pocketFive.value * 100) / 192) +
            Math.round((pocketMoreSix.value * 100) / 192)
          ).toFixed(2)
        ),
        moreSix: Number(
          Math.round((pocketMoreSix.value * 100) / 192).toFixed(2)
        ),
      },
    ])

    const pocketFour = computed(() => {
      const value =
        periodontalData.value?.firstFacialData.getSoundingValue(4) +
        periodontalData.value?.firstTongueData.getSoundingValue(4) +
        periodontalData.value?.secondFacialData.getSoundingValue(4) +
        periodontalData.value?.secondTongueData.getSoundingValue(4)

      return value
    })

    const pocketFive = computed(() => {
      const value =
        periodontalData.value?.firstFacialData.getSoundingValue(5) +
        periodontalData.value?.firstTongueData.getSoundingValue(5) +
        periodontalData.value?.secondFacialData.getSoundingValue(5) +
        periodontalData.value?.secondTongueData.getSoundingValue(5)

      return value
    })

    const pocketMoreSix = computed(() => {
      const value =
        periodontalData.value?.firstFacialData.getSoundingValue(6, true) +
        periodontalData.value?.firstTongueData.getSoundingValue(6, true) +
        periodontalData.value?.secondFacialData.getSoundingValue(6, true) +
        periodontalData.value?.secondTongueData.getSoundingValue(6, true)

      return value
    })

    const reportId = computed(() => Number(route.params.reportId))
    const patientId = computed(() => Number(route.params.patientId))
    const isPreviewMode = computed(
      (): boolean => route.query['view-mode'] === '1'
    )

    watch(reportId, (newReportId) => {
      if (route.name === 'PeriodontalPage') {
        if (newReportId) {
          getFullReport()
        } else {
          initEmptyPeriodontalData()
        }
      }
    })

    watch(
      () => isReportUpdated.value,
      () => {
        getFullReport()
        setUpdateReport(false)
      }
    )

    const initEmptyPeriodontalData = () => {
      periodontalData.value.firstFacialData = new PeriodontalData(
        JAW_TYPES.jawUp
      )
      periodontalData.value.firstTongueData = new PeriodontalData(
        JAW_TYPES.jawUp,
        true
      )
      periodontalData.value.secondFacialData = new PeriodontalData(
        JAW_TYPES.jawDown
      )
      periodontalData.value.secondTongueData = new PeriodontalData(
        JAW_TYPES.jawDown
      )

      periodontalData.value.firstFacialDiagram =
        periodontalData.value.firstFacialData.getDiagramData()
      periodontalData.value.firstTongueDiagram =
        periodontalData.value.firstTongueData.getDiagramData()
      periodontalData.value.secondFacialDiagram =
        periodontalData.value.secondFacialData.getDiagramData()
      periodontalData.value.secondTongueDiagram =
        periodontalData.value.secondTongueData.getDiagramData()

      consolidatedData.value.diabetes = false
      consolidatedData.value.smoking = false

      reportInfo.value.id = 0
      reportInfo.value.date_at = ''
      reportInfo.value.organization = undefined
      reportInfo.value.doctor = undefined

      comments.value = []
    }

    const getFullReport = async (): Promise<void> => {
      loading.value = true

      const [error, response] = await periodontalReportApi.getReport(
        reportId.value
      )

      if (error) {
        showErrorMessage(error)
      } else {
        const report = response.data.report
        reportInfo.value = report

        consolidatedData.value.smoking = Boolean(report.smoking) ?? false
        consolidatedData.value.diabetes = Boolean(report.diabetes) ?? false

        getReportData(JSON.parse(report.index))
        getComments(response.data)
        await getUserInfo(report.user_id)
        await getOrganizationInfo(report.organization_id)
        await getPatientEmail()
      }

      loading.value = false
    }

    const getReportData = (report: FullPeriodontalType) => {
      periodontalData.value.firstFacialData.setData(report.firstFacialData)
      periodontalData.value.firstTongueData.setData(report.firstTongueData)
      periodontalData.value.secondFacialData.setData(report.secondFacialData)
      periodontalData.value.secondTongueData.setData(report.secondTongueData)

      periodontalData.value.firstFacialDiagram =
        periodontalData.value.firstFacialData.getDiagramData()
      periodontalData.value.firstTongueDiagram =
        periodontalData.value.firstTongueData.getDiagramData()
      periodontalData.value.secondFacialDiagram =
        periodontalData.value.secondFacialData.getDiagramData()
      periodontalData.value.secondTongueDiagram =
        periodontalData.value.secondTongueData.getDiagramData()
    }

    const getUserInfo = async (userId: number): Promise<void> => {
      const [error, response] = await userApi.getUser(userId)

      if (error) {
        showErrorMessage(error)
      } else {
        reportInfo.value.doctor = response.data.user
      }
    }

    const getOrganizationInfo = async (
      organizationId: number
    ): Promise<void> => {
      const [error, response] = await organizationsApi.getOrganizationInfo(
        organizationId
      )

      if (error) {
        showErrorMessage(error)
      } else {
        reportInfo.value.organization = response.data.organization
      }
    }

    const getComments = (remoteComments: RemoteCommentsType): void => {
      commentTemplates.value = []
      comments.value = remoteComments.comments

      remoteComments.comment_templates.forEach((template) => {
        const newTemplate: CommentTemplateType = {
          template_id: template.id,
          template: template.template,
          isVisible: false,
        }

        commentTemplates.value.push(newTemplate)
      })
    }

    const getPatientEmail = async (): Promise<void> => {
      const [error, response] = await patientApi.getPatientInfo(
        Number(patientId.value)
      )

      if (error) {
        showErrorMessage(error)
      } else {
        email.value = response.data.patient.email
      }
    }

    onMounted(async (): Promise<void> => {
      if (reportId.value) {
        await getFullReport()
      } else {
        initEmptyPeriodontalData()
      }
    })

    return {
      reportId,
      date,
      reportInfo,
      loading,
      REPORT_VIEW,
      consolidatedData,
      periodontalData,
      comments,
      commentTemplates,
      isPreviewMode,
      consolidatedPlaque,
      consolidatedBleeding,
      consolidatedPockets,
      consolidatedPocketsTable,
      email,
      getFullReport,
    }
  },
})
